import { Box, Typography } from "@mui/material";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import React, { FC, useCallback, useEffect, useRef } from "react";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormUnregister,
  useFormState,
} from "react-hook-form";
import ConditionRuleEditor from "./ConditionRuleEditor";
import MarkdownEditor, { MarkdownEditorRef } from "components/common/MarkdownEditor";

const MARKDOWN_FIELD_NAME = "markdownContent";

const MarkdownFieldForm: FC<{
  formHeader: string;
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  unregister: UseFormUnregister<any>;
  specComponent: React.ReactNode;
}> = ({ formHeader, fieldId, control, setValue, unregister }) => {
  const { getFieldState, setFieldStateList } = useFieldCustomize();
  const state = getFieldState(fieldId);
  const { errors } = useFormState({ control });

  const markdownEditorRef = useRef<MarkdownEditorRef>(null);

  const handleMarkdownChange = useCallback(
    (value: string) => {
      setFieldStateList((items) =>
        items.map((item) =>
          item.id === state?.id
            ? {
                ...item,
                defaultValue: value,
                passValidate: value.trim() !== "",
              }
            : item
        )
      );
      setValue(MARKDOWN_FIELD_NAME, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setFieldStateList, setValue, state?.id]
  );

  useEffect(() => {
    const editor = markdownEditorRef.current;
    if (!editor) {
      return;
    }

    const subscribe = editor.onInput;
    const unsubscribe = subscribe?.(handleMarkdownChange);

    return () => {
      unsubscribe?.();
    };
  }, [handleMarkdownChange]);

  useEffect(() => {
    setValue(MARKDOWN_FIELD_NAME, (state?.defaultValue as string) || "", {
      shouldValidate: true,
    });
  }, [setValue, state?.id, state?.defaultValue]);

  const markdownError = errors?.[MARKDOWN_FIELD_NAME]?.message as string | undefined;

  return (
    <>
      <div
        style={{
          margin: 8,
          paddingTop: 20,
          border: "1px solid #DEDEDE",
          borderRadius: 4,
          padding: 25,
        }}
      >
        <Typography
          variant="h6"
          align="center"
        >
          {formHeader}
        </Typography>

        <Controller
          name={MARKDOWN_FIELD_NAME}
          control={control}
          defaultValue={(state?.defaultValue as string) || ""}
          rules={{
            required: "This field is required.",
            validate: (value: string) => (value?.trim() ? true : "This field is required."),
          }}
          render={() => <></>}
        />

        <Box>
          <MarkdownEditor
            ref={markdownEditorRef}
            defaultValue={(state?.defaultValue as string) || ""}
            height={480}
          />
        </Box>
        {markdownError ? (
          <Typography
            color="error"
            style={{ marginTop: 8, fontSize: 12, lineHeight: "18px" }}
          >
            {markdownError}
          </Typography>
        ) : null}
      </div>
      <ConditionRuleEditor
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
      />
    </>
  );
};

export default MarkdownFieldForm;
